import { Navigate } from 'react-router-dom';
import { PageSpinner } from '@/components/ui/page-spinner';
import { useIsSuperAdmin } from '@/lib/permissions';
import { useCurrentUserOrgRole } from '@/hooks/useCurrentUserOrgRole';
import { useOrgBasePath } from '@/hooks/useOrgNavigate';

interface RequireOrgAdminProps {
  children: React.ReactNode;
}

/**
 * Route guard for org-admin-only areas (e.g. /settings/usage).
 *
 * Modelled on RequireSuperAdmin: nav items carrying `requiresOrgAdmin` are
 * already hidden for members, but hiding is not enforcement — without this
 * guard a member deep-linking to the URL mounts the page (and its queries) and
 * gets a raw 403 from the API instead of a redirect. RLS / the backend's
 * admin check remain the ultimate backstop.
 *
 * Super admins pass: they have no membership row in the org (so
 * `useCurrentUserOrgRole` reports `isAdmin: false`) yet must still reach the
 * page. Fails closed while either check is in flight. Non-admins are sent to
 * the org dashboard.
 */
export function RequireOrgAdmin({ children }: RequireOrgAdminProps) {
  const { data: isSuperAdmin, isLoading: isSuperAdminLoading } = useIsSuperAdmin();
  const { isAdmin, isLoading: isRoleLoading } = useCurrentUserOrgRole();
  const orgBasePath = useOrgBasePath();

  if (isSuperAdminLoading || isRoleLoading) {
    return <PageSpinner />;
  }

  if (!isAdmin && isSuperAdmin !== true) {
    return <Navigate to={orgBasePath ? `${orgBasePath}/dashboard` : '/'} replace />;
  }

  return <>{children}</>;
}
